import { Link, router, usePage } from "@inertiajs/react"
import { CiSearch } from "react-icons/ci"
import { AiOutlineLoading3Quarters } from "react-icons/ai"
import { ArrowRight } from "lucide-react"
import { useState, useRef, useEffect } from "react"
import { OverlayPanel } from "primereact/overlaypanel"
import axios from "axios"
import { ItemList } from "./GetItem/ItemList"

export function SearchBarV2({ showSearchBar, toggleSearch }) {
    const { url, props } = usePage()
    const devKey = props?.devKey
    const currentQuery =
        new URLSearchParams(url.split("?")[1] ?? "").get("q") ?? ""
    const [data, setData] = useState([])
    const [loading, setLoading] = useState(false)
    const [searchString, setSearchString] = useState(currentQuery)
    const isFirstRender = useRef(true)
    // Set when a suggestion is being clicked so the input's blur doesn't commit a
    // /search?q= visit and cancel the suggestion's own navigation.
    const suppressBlurSubmit = useRef(false)
    const handleInputChange = async (e) => {
        const searchText = e.target.value

        setSearchString(searchText)
    }

    useEffect(() => {
        // Skip the mount run so a search term seeded from the URL (e.g. landing on
        // /search?q=…) populates the input without firing a suggestions fetch.
        if (isFirstRender.current) {
            isFirstRender.current = false
            return
        }

        const timer = setTimeout(() => {
            if (searchString.length === 0) {
                setData([])
                return
            }
            // Suggestions are fetched as JSON so typing never touches the browser URL
            // or history — only `q` (set on submit/blur) belongs in the address bar.
            setLoading(true)
            axios
                .get("/api/search-suggestions", {
                    params: { q: searchString },
                    headers: { "Dev-Key": devKey },
                })
                .then((res) => setData(res.data.search_results ?? []))
                .catch(() => setData([]))
                .finally(() => setLoading(false))
        }, 800)

        return () => {
            clearTimeout(timer)
        }
    }, [searchString])

    // Navigate to /search with the typed term. When the user is already on the
    // search page, the current active filters (and any other query params) are
    // retained and only `q` is updated; pagination is reset to the first page.
    // A new search always starts fresh: navigate to /search with only `q`, so any
    // active filters (brand/caliber/category/…) and pagination are cleared.
    const submitSearch = () => {
        const term = searchString.trim()
        if (term.length === 0) {
            router.visit("/search")
            return
        }
        const params = new URLSearchParams({ q: term })
        router.visit(`/search?${params.toString()}`)
    }

    const opSearch = useRef(null)

    function showSearch(e) {
        toggleSearch(!showSearchBar)
        opSearch.current.toggle(e)
    }

    const [showSuggestions, setShowSuggestions] = useState(false)

    const closeSuggestionsWithDelay = () => {
        setTimeout(() => {
            setShowSuggestions(false)
        }, 200)
    }

    // Commit the typed term to `q` when leaving the field — unless the blur was
    // caused by clicking a suggestion (let that navigation win) or the term is
    // unchanged/empty (nothing to commit).
    const handleInputBlur = () => {
        if (suppressBlurSubmit.current) {
            suppressBlurSubmit.current = false
            closeSuggestionsWithDelay()
            return
        }

        const term = searchString.trim()
        if (term.length > 0 && term !== currentQuery) {
            submitSearch()
        }
        closeSuggestionsWithDelay()
    }

    return (
        <>
            <div className="hidden w-[200px] desktop:flex">
                <form
                    method="GET"
                    className=""
                    onSubmit={(e) => {
                        e.preventDefault()
                        submitSearch()
                    }}
                >
                    <div className="relative flex items-center w-full max-w-[200px] me-2 sm:me-5">
                        <span className="absolute left-3">
                            <CiSearch
                                className="w-[20px] h-[20px]"
                                onClick={() =>
                                    window.document
                                        .getElementById("top-search")
                                        .focus()
                                }
                            />
                        </span>
                        <input
                            type="text"
                            id="top-search"
                            className="border border-[#FFF] sm:border-[#E5E7EB] max-w-[24px] sm:max-w-full focus:max-w-full rounded-lg focus:bg-neutral-100 text-[15px] font-medium ps-11 w-full sm:ps-11 py-[11px]"
                            name="q"
                            value={searchString}
                            placeholder="Search Website"
                            onChange={handleInputChange}
                            onBlur={handleInputBlur}
                            onFocus={() => setShowSuggestions(true)}
                        />
                        {loading && (
                            <span className="absolute right-3">
                                <AiOutlineLoading3Quarters className="w-[20px] h-[20px] animate-spin" />
                            </span>
                        )}

                        {showSuggestions && data?.length > 0 && (
                            <div
                                style={{ zIndex: 1000 }}
                                className="absolute flex flex-col top-[70px] left-0 bg-white w-[300px] rounded-xl overflow-hidden border border-[#E5E7EB] p-2 z-10"
                                onMouseDown={() => {
                                    suppressBlurSubmit.current = true
                                }}
                            >
                                {data.map((item) => (
                                    <ItemList item={item} key={item.id} />
                                ))}
                            </div>
                        )}
                    </div>
                </form>
            </div>

            <div className="block desktop:hidden">
                <button
                    onClick={(e) => showSearch(e)}
                    type="button"
                    aria-label="Search"
                    className="inline-flex items-center px-3 py-2 text-sm font-medium leading-4 text-gray-500 transition duration-150 ease-in-out bg-white border border-transparent rounded-md hover:text-gray-700 focus:outline-none"
                >
                    <svg
                        width="24"
                        height="25"
                        viewBox="0 0 24 25"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M11 19.5C15.4183 19.5 19 15.9183 19 11.5C19 7.08172 15.4183 3.5 11 3.5C6.58172 3.5 3 7.08172 3 11.5C3 15.9183 6.58172 19.5 11 19.5Z"
                            stroke="black"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                        <path
                            d="M21 21.5L16.7 17.2"
                            stroke="black"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </button>

                <OverlayPanel
                    ref={opSearch}
                    closeOnEscape
                    className="absolute !left-0 top-0 right-0"
                    onHide={() => {
                        toggleSearch(false)
                    }}
                >
                    <form
                        method="GET"
                        className=""
                        onSubmit={(e) => {
                            e.preventDefault()
                            submitSearch()
                        }}
                    >
                        <div className="relative flex items-center w-full me-2 sm:me-12">
                            <span className="absolute left-3">
                                <CiSearch className="w-[30px] h-[30px]" />
                            </span>
                            <input
                                type="text"
                                id="top-search"
                                className="border border-[#E5E7EB] rounded-lg bg-neutral-100 text-[15px] font-medium w-full ps-11 pe-14 py-[14px] h-[46px]"
                                name="q"
                                value={searchString}
                                placeholder="Search Website"
                                onChange={handleInputChange}
                                onFocus={() => setShowSuggestions(true)}
                                onBlur={handleInputBlur}
                            />

                            <button
                                type="submit"
                                aria-label="Search"
                                onMouseDown={() => {
                                    suppressBlurSubmit.current = true
                                }}
                                className="absolute flex items-center justify-center text-white transition-colors bg-black rounded-full right-2 w-9 h-9 hover:bg-gray-800"
                            >
                                <ArrowRight className="w-5 h-5" />
                            </button>

                            {showSuggestions && data?.length > 0 && (
                                <div
                                    style={{ zIndex: 1000 }}
                                    className="absolute flex flex-col top-[70px] left-0 bg-white w-full rounded-xl overflow-hidden border border-[#E5E7EB] p-2 z-10"
                                    onMouseDown={() => {
                                        suppressBlurSubmit.current = true
                                    }}
                                >
                                    {data.map((item) => (
                                        <ItemList item={item} key={item.id} />
                                    ))}
                                </div>
                            )}
                        </div>
                    </form>
                </OverlayPanel>
            </div>
        </>
    )
}
